import { fetchApi } from '@/services/api';
import { startSyncForecastBootstrap, type SyncForecastResult } from './syncForecast';

import type {
  ConsensusOverviewPayload,
  CreatePlanningCyclePayload,
  Paginated,
  PlanningCycleRow,
} from '@/modules/demand-planning/types/consensus.types';

export const planningCyclesService = {
  list(): Promise<PlanningCycleRow[]> {
    return fetchApi<Paginated<PlanningCycleRow>>('demand-planning/planning-cycles/', {
      params: { page_size: 500, ordering: '-period_start' },
    }).then((data) => data.results ?? []);
  },

  createCycleWithVersion(payload: CreatePlanningCyclePayload): Promise<number> {
    return fetchApi<{ id: number }>('demand-planning/planning-cycles/', {
      method: 'POST',
      body: {
        name: payload.name,
        cycle_type: payload.cycle_type,
        status: 'open',
        period_start: payload.period_start,
        period_end: payload.period_end,
        forecast_horizon_start: payload.period_start,
        forecast_horizon_end: payload.period_end,
      },
    }).then(async (cycle) => {
      await fetchApi('demand-planning/plan-versions/', {
        method: 'POST',
        body: {
          planning_cycle: cycle.id,
          version_name: payload.version_name,
          version_number: 1,
          status: 'draft',
          is_baseline: true,
          is_current: true,
        },
      });
      return cycle.id;
    });
  },

  updateStatus(cycleId: number, status: string): Promise<void> {
    return fetchApi(`demand-planning/planning-cycles/${cycleId}/`, {
      method: 'PATCH',
      body: { status },
    }).then(() => undefined);
  },

  getOverview(
    planningCycleId: number,
    options?: { include?: string },
  ): Promise<ConsensusOverviewPayload> {
    const params: Record<string, string | number> = { planning_cycle: planningCycleId };
    if (options?.include) params.include = options.include;
    return fetchApi<ConsensusOverviewPayload>('demand-planning/consensus-lines/overview/', {
      params,
    });
  },

  getOverviewFromParams(searchParams: URLSearchParams): Promise<ConsensusOverviewPayload> {
    const params: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      const existing = params[key];
      if (existing === undefined) {
        params[key] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        params[key] = [existing, value];
      }
    });
    return fetchApi<ConsensusOverviewPayload>('demand-planning/consensus-lines/overview/', {
      params,
    });
  },

  syncForecastAndBootstrap(
    cycleId: number,
    options?: { force?: boolean; wait?: boolean },
  ): Promise<SyncForecastResult> {
    return startSyncForecastBootstrap(cycleId, options);
  },

  duplicate(cycleId: number, name?: string): Promise<PlanningCycleRow> {
    return fetchApi<PlanningCycleRow>(`demand-planning/planning-cycles/${cycleId}/duplicate/`, {
      method: 'POST',
      body: name ? { name } : {},
    });
  },

  publishDemandPlan(
    cycleId: number,
    scenarioId: number | null,
  ): Promise<{ demand_plan?: ConsensusOverviewPayload['demand_plan'] }> {
    return fetchApi(`demand-planning/planning-cycles/${cycleId}/publish-demand-plan/`, {
      method: 'POST',
      body: { scenario_id: scenarioId },
    });
  },
};
